import { ComponentType, lazy, useEffect, useState } from "react";
import { USER } from "./user";

type PopinModule = Promise<{ default: ComponentType<any> }>;
type PopinLoader = () => PopinModule;

function createPopinLoader(loadPopin: PopinLoader): PopinLoader {
  let popinModule: PopinModule | undefined;
  return () => popinModule ??= loadPopin();
}

const popinLoaders: Record<popinsNames, PopinLoader> = {
  login: createPopinLoader(() => import("../Popins/Login")),
  viewer: createPopinLoader(() => import("../Popins/EphemeViewer")),
  tokens: createPopinLoader(() => import("../Popins/Tokens")),
  disclaimer: createPopinLoader(() => import("../Popins/Disclaimer")),
  subscribe: createPopinLoader(() => import("../Popins/Subscription")),
  certificate: createPopinLoader(() => import("../Popins/Certificate")),
  edit: createPopinLoader(() => import("../Popins/EditProfile")),
  uploading: createPopinLoader(() => import("../Popins/Uploading")),
  contact: createPopinLoader(() => import("../Popins/Contact")),
  trophy: createPopinLoader(() => import("../Popins/Trophy")),
  blocked: createPopinLoader(() => import("../Popins/Blocked")),
  contentprovider: createPopinLoader(() => import("../Popins/ContentProvider")),
  paymentrequired: createPopinLoader(() => import("../Popins/PaymentRequired")),
  emailconfirm: createPopinLoader(() => import("../Popins/EmailConfirm")),
  chatcontent: createPopinLoader(() => import("../Popins/ChatContent")),
  chatreport: createPopinLoader(() => import("../Popins/ChatReport")),
  underage: createPopinLoader(() => import("../Popins/Underage")),
  fanclub: createPopinLoader(() => import("../Popins/Fanclub")),
  chaterror: createPopinLoader(() => import("../Popins/ChatError")),
  forcelogin: createPopinLoader(() => import("../Popins/ForceLogin")),
  commonboost: createPopinLoader(() => import("../Popins/CommonBoost")),
  pickvalidator: createPopinLoader(() => import("../Popins/Underage/pickValidator")),
  addcard: createPopinLoader(() => import("../Popins/Underage/addCard")),
  paymentsuccessful: createPopinLoader(() => import("../Popins/PaymentSuccessful")),
  dailyspin: createPopinLoader(() => import("../Popins/DailySpin")),
};

const popinsObj: {
  [Key in popinsNames]: ComponentType<any>;
} = Object.fromEntries(
  Object.entries(popinLoaders).map(([name, loader]) => [name, lazy(loader)]),
) as unknown as Record<popinsNames, ComponentType<any>>;

export type popinsNames = "dailyspin"|"paymentsuccessful"|"addcard"|"pickvalidator"|"commonboost"|"chaterror"|"fanclub"|"underage"|"chatreport"|"chatcontent"|"contentprovider"|"blocked"|"edit"|"login"|"paymentrequired"|"viewer"|"tokens"|"disclaimer"|"subscribe"|"certificate"|"uploading"|"contact"|"trophy"|"emailconfirm"|"forcelogin"

export function preloadPopins() {
  return Promise.allSettled(Object.values(popinLoaders).map((loadPopin) => loadPopin()));
}

export function preloadPopin(name: popinsNames) {
  return popinLoaders[name]();
}

export type PopinEntry = {
  name: popinsNames;
  Component: ComponentType<any>;
  props: any;
  /** Monotonic — the holder keys on it so a replaced popin remounts cleanly. */
  key: number;
};

/**
 * The popin stack. One queue of {name, Component, props} entries — a popin and its
 * props travel together, so the email gate (or anything else that reroutes a
 * request) can never desynchronise them. The holder renders the head of the queue.
 *
 * Three verbs:
 * - display: queue a popin. Opened over another it waits its turn.
 * - close:   dismiss the open popin; the next queued one appears.
 * - replace: swap the open popin for the next step of its chain — the payment,
 *   identity and provider flows hop with this so a step never queues behind the
 *   popin it is meant to succeed.
 */
class Popins {
  queue: PopinEntry[] = [];
  // A set, not a slot: the holder and the offer-code prompt both listen, and a
  // single field would let whichever mounted last starve the other (the bug
  // that once made every popin silently fail to open).
  private listeners = new Set<(entries: PopinEntry[]) => void>();
  private seq = 0;

  subscribe(listener: (entries: PopinEntry[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    const snapshot = [...this.queue];
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private entry(name: popinsNames, properties: any): PopinEntry {
    // The email gate: tokens and subscriptions need a confirmed address. The gate
    // swaps the request for emailconfirm and hands it the original request, so the
    // confirmation can resume exactly what the member was doing — props intact.
    if ((name === "tokens" || name === "subscribe") && !USER.informations?.is_email_confirmed) {
      return {
        name: "emailconfirm",
        Component: popinsObj.emailconfirm,
        props: { unlock: name, resume: { name, props: properties } },
        key: ++this.seq,
      };
    }
    return { name, Component: popinsObj[name], props: properties, key: ++this.seq };
  }

  display = (name: popinsNames, properties = {}) => {
    this.queue.push(this.entry(name, properties));
    this.notify();
  };

  close = () => {
    this.queue.shift();
    this.notify();
  };

  replace = (name: popinsNames, properties = {}) => {
    this.queue.splice(0, 1, this.entry(name, properties));
    this.notify();
  };
}

const POPINS = new Popins();

export const closeCurrentPopin = POPINS.close;

/** Close the open popin and open the chain's next step in its place. */
export const replaceCurrentPopin = POPINS.replace;

export function displayPopin() {
  return POPINS.display;
}

(window as any).displayPopin = displayPopin;
(window as any).preloadPopin = preloadPopin;

(window as any).displayVerificationEmailPopin = () =>
  displayPopin()("emailconfirm");

export function useListenPopins() {
  const [popins, setPopins] = useState<PopinEntry[]>(POPINS.queue);

  useEffect(() => {
    const unsubscribe = POPINS.subscribe(setPopins);
    // A popin queued between the first render and this effect would otherwise be
    // lost — sync once now that the listener is attached.
    setPopins([...POPINS.queue]);
    return unsubscribe;
  }, []);

  return popins;
}
